import React from 'react';
import {Container, Grid, Icon, Paper, Typography} from "@mui/material";
import {lightBlue} from "@mui/material/colors";
import PrimaryButton from "../../Common/PrimaryButton";
import {differenceInHours, format, parse} from "date-fns";
import {useHistory} from 'react-router-dom';
import {getTripFormattedDate} from '../../../utils/utils';
import {styled} from "@mui/material/styles";

const PREFIX = 'TripCard';
const classes = {
    root: `${PREFIX}-root`,
    container: `${PREFIX}-container`,
    tripImage: `${PREFIX}-trip-image`,
    status: `${PREFIX}-status`,
    hold: `${PREFIX}-hold`,
    waiting: `${PREFIX}-waiting`,
    paid: `${PREFIX}-paid`,
    tripTitle: `${PREFIX}-tripTitle`,
    tripDetailsContainer: `${PREFIX}-tripDetailsContainer`,
    tripDetails: `${PREFIX}-tripDetails`,
    footerInfo: `${PREFIX}-footerInfo`,
}
const PaperTrip = styled(Paper)(({theme}) => ({
    [`&.${classes.root}`]: {
        marginTop: '20px',
    },
    [`& .${classes.status}`]: {
        color: 'white',
        padding: '5px',
        textTransform: 'uppercase',
        fontWeight: '600',
        fontSize: '1.4rem',
        letterSpacing: '1.2px',
    },
    [`& .${classes.container}`]: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
    },
    [`& .${classes.tripImage}`]: {
        objectFit: 'cover',
        height: '100%',
    },[`& .${classes.hold}`]: {
        backgroundColor: 'lightgray',
    },
    [`& .${classes.waiting}`]: {
        backgroundColor: theme.palette.warning.main,
    },
    [`& .${classes.paid}`]: {
        backgroundColor: theme.palette.success.main,
    },
    [`& .${classes.tripTitle}`]: {
        fontSize: '2.4rem',
        letterSpacing: '1.2px'
    },
    [`& .${classes.tripDetailsContainer}`]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    [`& .${classes.tripDetails}`]: {
        fontSize: '1.2rem',
        marginLeft: '5px'
    },
    [`& .${classes.footerInfo}`]: {
        fontWeight: '600'
    }
}))

const TripCard = ({booking, booking: {trip, berths}, update}) => {
    const history = useHistory();

    const getStatus = () => {
        if (booking['bookStateFk'] === 1) {
            if (booking.TotalPaid < booking.DealerPrice)
                return 'waiting';
            return 'paid';
        }
        if (booking['bookStateFk'] === 2)
            return 'hold';
    };

    const getStatusText = () => {
        if (booking['bookStateFk'] === 1) {
            if (booking.TotalPaid < booking.DealerPrice)
                return 'Awaiting Payment';
            return 'Paid';
        }
        if (booking['bookStateFk'] === 2) {
            const bookingTime = format(parse(booking.bookDate, 'yyyy-MM-dd HH:mm:ss', new Date()), 'HH:mm:ss');
            const depositDate = parse(`${booking.DepositDate} ${bookingTime}`, 'yyyy-MM-dd HH:mm:ss', new Date());
            const difference = differenceInHours(depositDate, new Date());
            const remainingTime = difference > 0 ? `${difference} Hours left` : 'Contact our office';
            return `On Hold - ${remainingTime}`;
        }
    };

    const handleReview = () => {
        history.push(`/booking/my-bookings/${booking.id}`);
    };

    const locationIcon = trip?.tripClassName !== 'San Ignacio Lagoon' ?
        <Icon style={{color: lightBlue[500]}} className={'tripInfoIcon'}>directions_boat</Icon> :
        <Icon style={{color: lightBlue[500], width: 35}} className={'tripInfoIcon fa fa-campground'}/>;

    return (
        <PaperTrip className={classes.root} elevation={3} sx={{mx: {xs: '1.6rem', lg: '0'}}}>
            <Grid container>
                <Grid item sm={12} md={3} style={{width: '100%'}}>
                    <div className={classes.container}>
                        <img className={classes.tripImage} src={trip.tripImageCard} alt={trip.shortcomment}/>
                    </div>
                </Grid>
                <Grid item sm={12} md={9}>
                    <Container>
                        <div style={{margin: '0 10px'}}>
                            {update ? (<p style={{marginBottom: '10px'}}>
                                <span className={`${classes.status} ${classes[getStatus()]}`}>
                                    {getStatusText()}
                                </span>
                            </p>) : <br/>}
                            <Typography variant="h3" className={classes.tripTitle}>
                                {trip.tripClassName} - {trip.shortcomment}
                            </Typography>
                            <Grid container spacing={3} style={{paddingTop: '10px'}}>
                                <Grid item className={classes.tripDetailsContainer} xs={12} sm={12} md={3}>
                                    <Icon style={{color: lightBlue[500]}}
                                          className={'tripInfoIcon far fa-calendar-alt'}/>
                                    <span className={classes.tripDetails}>
                                        {getTripFormattedDate(trip.boarddate, trip.returndate)}
                                    </span>
                                </Grid>
                                <Grid item className={classes.tripDetailsContainer} xs={12} sm={12} md={3}>
                                    <Icon style={{color: lightBlue[500]}} className={'tripInfoIcon'}>nights_stay</Icon>
                                    <span className={classes.tripDetails}>
                                        {trip.length} nights {trip.length + 1} days
                                    </span>
                                </Grid>
                                <Grid item className={classes.tripDetailsContainer} xs={12} sm={12} md={3}>
                                    {locationIcon}
                                    <span className={classes.tripDetails}>{trip.location.location}</span>
                                </Grid>
                                <Grid item className={classes.tripDetailsContainer} xs={12} sm={12} md={3}>
                                    <PrimaryButton onClick={handleReview}>{update ? 'Update' : 'View'}</PrimaryButton>
                                </Grid>
                            </Grid>
                            <Grid container direction="column" style={{margin: '10px 0'}}>
                                <Typography className={classes.footerInfo} variant="h5">
                                    Booking Confirmation #: {booking.Confirmation}
                                </Typography>
                                <Typography className={classes.footerInfo}
                                            variant="h5">Accommodation:
                                    {berths.map(berth => (<span key={berth.index}>
                                        &nbsp;{berth.count} {berth.count > 1 ? 'berths' : 'berth'} in <span dangerouslySetInnerHTML={{__html: berth.title}} />.
                                    </span>))}
                                </Typography>
                            </Grid>
                        </div>
                    </Container>
                </Grid>
            </Grid>
        </PaperTrip>
    );
};

export default TripCard;